import { z } from 'zod'
import { LOCAL_DATE_RE } from './dates.js'
import { EventSchema } from './events.js'
import { FlagsSchema } from './flags.js'
import { DailyGorevlerSchema } from './gorev.js'
import {
  AGE_BANDS,
  BADGE_IDS,
  CHALLENGE_IDS,
  CONTENT_ID_RE,
  DECOR_SLOTS,
  GAME_IDS,
  LESSON_IDS,
  MODES,
  SLOTS,
  STATIC_RECAP_QUESTION_IDS,
  WINDOW_STATES,
} from './ids.js'

/**
 * Every request and response body in docs/API.md. The API mounts these through
 * fastify-type-provider-zod, so the schema is the validation and there is no second copy.
 * WP-E extends this file additively (WORKPLAN §3).
 */

export const localDate = z.string().regex(LOCAL_DATE_RE)
const contentId = z.string().regex(CONTENT_ID_RE)
const timeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM')

export const ApiErrorSchema = z.object({
  error: z.object({ code: z.string(), message: z.string() }),
})
export type ApiError = z.infer<typeof ApiErrorSchema>

export const WindowsConfigSchema = z.strictObject({
  /** Local times, inclusive start and exclusive end. The evening window may cross midnight. */
  morning: z.tuple([timeOfDay, timeOfDay]),
  evening: z.tuple([timeOfDay, timeOfDay]),
  nudge: z.boolean(),
  weekendQuiet: z.boolean(),
  /**
   * MASTERPLAN §10: the weekly summary is sent by mail only when the parent asks for it, and
   * MONETIZATION §2 keeps it behind Aile Plus. Off unless both say yes. Added by WP-E; rows
   * written before it default to false.
   */
  weeklyEmail: z.boolean().default(false),
})
export type WindowsConfig = z.infer<typeof WindowsConfigSchema>

export const DEFAULT_WINDOWS: WindowsConfig = {
  morning: ['05:00', '12:00'],
  evening: ['18:00', '00:30'],
  nudge: true,
  weekendQuiet: false,
  weeklyEmail: false,
}

export const CONSENT_TYPES = ['kvkk', 'camera', 'research'] as const
export const ConsentSchema = z.object({
  type: z.enum(CONSENT_TYPES),
  version: z.string().min(1).max(16),
  at: z.string(),
})

export const ParentSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  emailVerified: z.boolean(),
  createdAt: z.string(),
  locale: z.enum(['tr', 'en']),
  windows: WindowsConfigSchema,
  researchConsent: z.boolean(),
  consents: z.array(ConsentSchema),
  hasPin: z.boolean(),
})
export type Parent = z.infer<typeof ParentSchema>

export const SessionSchema = z.object({
  token: z.string(),
  expiresAt: z.string(),
})

export const ChildSchema = z.object({
  id: z.uuid(),
  nickname: z.string(),
  ageBand: z.enum(AGE_BANDS),
  avatarId: z.string(),
  petName: z.string(),
  mode: z.enum(MODES),
  reviewMode: z.boolean(),
  createdAt: z.string(),
})
export type Child = z.infer<typeof ChildSchema>

export const EntitlementSchema = z.object({
  plan: z.enum(['free', 'plus']),
  source: z.enum(['none', 'web', 'apple', 'google']),
  validUntil: z.string().nullable(),
  willRenew: z.boolean(),
})
export type Entitlement = z.infer<typeof EntitlementSchema>

/* -------------------------------------------------------------------- auth */

export const RegisterBody = z.strictObject({
  email: z.email().max(254),
  password: z.string().min(10).max(200),
  locale: z.enum(['tr', 'en']).default('tr'),
})
export const LoginBody = z.strictObject({
  email: z.email().max(254),
  password: z.string().min(1).max(200),
})
export const AuthResponse = z.object({ parent: ParentSchema, session: SessionSchema })
export const TokenBody = z.strictObject({ token: z.string().min(10).max(200) })
export const ForgotBody = z.strictObject({ email: z.email().max(254) })
export const ResetBody = z.strictObject({
  token: z.string().min(10).max(200),
  password: z.string().min(10).max(200),
})
export const OkResponse = z.object({ ok: z.literal(true) })
export const MeResponse = z.object({
  parent: ParentSchema,
  children: z.array(ChildSchema),
  flags: FlagsSchema,
  entitlement: EntitlementSchema,
})

/* ---------------------------------------------------------- consents and pin */

export const ConsentBody = z.strictObject({
  type: z.enum(CONSENT_TYPES),
  version: z.string().min(1).max(16),
  granted: z.boolean(),
})
export const PinBody = z.strictObject({ pin: z.string().regex(/^\d{4}$/) })
export const PinResponse = z.object({ ok: z.boolean(), pinHash: z.string().optional() })

/* ---------------------------------------------------------------- children */

export const ChildCreateBody = z.strictObject({
  nickname: z.string().min(1).max(16),
  ageBand: z.enum(AGE_BANDS),
  avatarId: contentId,
  petName: z.string().min(1).max(12),
})
export const ChildPatchBody = z
  .strictObject({
    nickname: z.string().min(1).max(16),
    ageBand: z.enum(AGE_BANDS),
    avatarId: contentId,
    petName: z.string().min(1).max(12),
    mode: z.enum(MODES),
  })
  .partial()
export const ReviewModeBody = z.strictObject({ code: z.string().min(4).max(64) })

/* ------------------------------------------------------------------- parent */

/**
 * The reminder windows are the parent's own setting (MASTERPLAN §11.3) and the cron reads
 * them, so there has to be a way to change them. Added by WP-E; API.md §1 shows the field on
 * the parent but names no endpoint for it.
 */
export const ParentPatchBody = z
  .strictObject({
    windows: WindowsConfigSchema,
    locale: z.enum(['tr', 'en']),
  })
  .partial()

/* -------------------------------------------------------------------- state */

const windowState = z.enum(WINDOW_STATES)
const windowPair = z.object({ morning: windowState, evening: windowState })

export const ChildStateSchema = z.object({
  profile: z.object({
    nickname: z.string(),
    ageBand: z.enum(AGE_BANDS),
    mode: z.enum(MODES),
    avatarId: z.string(),
    petName: z.string(),
    reviewMode: z.boolean(),
  }),
  stars: z.number(),
  level: z.number(),
  starsToNext: z.number(),
  parilti: z.number(),
  shine: z.number(),
  streak: z.object({
    current: z.number(),
    best: z.number(),
    freezeTokens: z.number(),
    lastVerifiedDate: z.string().nullable(),
    freezeUsedDates: z.array(z.string()),
  }),
  today: z.object({
    date: z.string(),
    morning: windowState,
    evening: windowState,
    extraCount: z.number(),
    minigamePlays: z.number(),
    fedCount: z.number(),
    tamGun: z.boolean(),
    firstOpenRewarded: z.boolean(),
  }),
  calendar: z.record(z.string(), windowPair),
  lessons: z.record(
    z.enum(LESSON_IDS),
    z.object({
      started: z.boolean(),
      completed: z.boolean(),
      quizFirstTryCorrect: z.boolean().nullable(),
      chestOpened: z.boolean(),
    }),
  ),
  recap: z.object({
    unlocked: z.boolean(),
    completedCount: z.number(),
    lastCompletedDate: z.string().nullable(),
    nextRewardDate: z.string().nullable(),
    questions: z.record(
      z.enum(STATIC_RECAP_QUESTION_IDS),
      z.object({ firstTryCorrect: z.boolean().nullable() }),
    ),
  }),
  gorev: z.object({
    today: z.object({
      date: z.string(),
      items: DailyGorevlerSchema,
      done: z.array(z.enum(CHALLENGE_IDS)),
      ucluRewarded: z.boolean(),
    }),
    totalDone: z.number(),
  }),
  minigames: z.record(
    z.enum(GAME_IDS),
    z.object({ tutorialDone: z.boolean(), bestScore: z.number() }),
  ),
  inventory: z.object({
    foods: z.record(z.string(), z.number()),
    items: z.array(z.string()),
  }),
  equipped: z.partialRecord(z.enum(SLOTS), z.string()),
  decor: z.partialRecord(z.enum(DECOR_SLOTS), z.string()),
  badges: z.array(z.enum(BADGE_IDS)),
  observations: z.record(
    z.string(),
    z.object({ thumb: z.boolean(), mouthBreath: z.boolean(), tongueThrust: z.boolean() }),
  ),
  lastEventTs: z.number(),
})

export const ChildStateResponse = z.object({ state: ChildStateSchema, seq: z.number() })

/* --------------------------------------------------------------------- sync */

export const SYNC_PUSH_MAX = 200
export const SYNC_PULL_PAGE = 500

export const SyncPushBody = z.strictObject({
  childId: z.uuid(),
  events: z.array(EventSchema).min(1).max(SYNC_PUSH_MAX),
})
export const SYNC_REJECT_REASONS = ['schema', 'child_mismatch', 'conflict', 'unknown'] as const
export const SyncPushResponse = z.object({
  accepted: z.array(z.object({ id: z.uuid(), seq: z.number().int().positive() })),
  rejected: z.array(z.object({ id: z.string(), reason: z.enum(SYNC_REJECT_REASONS) })),
})
export const SyncPullQuery = z.object({
  childId: z.uuid(),
  sinceSeq: z.coerce.number().int().min(0).default(0),
})
export const SyncPullResponse = z.object({
  events: z.array(EventSchema),
  latestSeq: z.number().int().min(0),
  nextSince: z.number().int().min(0).nullable(),
})

/* ------------------------------------------------------------- entitlements */

export const CheckoutBody = z.strictObject({ plan: z.enum(['monthly', 'yearly']) })
export const CheckoutResponse = z.object({ url: z.string() })
export const RestoreBody = z.strictObject({
  platform: z.enum(['apple', 'google']),
  receipt: z.string().min(1).max(20_000),
})

/* -------------------------------------------------------------------- share */

export const SHARE_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
export const SHARE_CODE_LENGTH = 8
export const SHARE_CODE_DAYS = 30

export const ShareCreateBody = z.strictObject({ childId: z.uuid() })
export const ShareCreateResponse = z.object({
  code: z.string().length(SHARE_CODE_LENGTH),
  expiresAt: z.string(),
})
export const ShareViewResponse = z.object({
  nickname: z.string(),
  ageBand: z.enum(AGE_BANDS),
  last90: z.array(z.object({ date: localDate, morning: windowState, evening: windowState })),
  streak: z.object({ current: z.number(), best: z.number() }),
  lessons: z.array(z.object({ id: z.enum(LESSON_IDS), completed: z.boolean() })),
  observations: z.array(
    z.object({
      month: z.string(),
      thumb: z.boolean(),
      mouthBreath: z.boolean(),
      tongueThrust: z.boolean(),
    }),
  ),
  /** W3-OVERHAUL §6 W3-4: the same two counts the parent panel shows, nothing else. */
  recap: z.object({ completedCount: z.number(), lastCompletedDate: z.string().nullable() }),
  gorev: z.object({ doneToday: z.number(), totalDone: z.number() }),
})

/* --------------------------------------------------------------------- push */

export const PushSubscribeBody = z.strictObject({
  childId: z.uuid(),
  subscription: z.object({
    endpoint: z.url().max(2000),
    expirationTime: z.number().nullable().optional(),
    keys: z.object({ p256dh: z.string().max(200), auth: z.string().max(200) }),
  }),
})
export const PushUnsubscribeBody = z.strictObject({ endpoint: z.url().max(2000) })

/* --------------------------------------------------------------------- misc */

export const HealthResponse = z.object({
  ok: z.boolean(),
  sha: z.string(),
  migrations: z.number().int().min(0),
  db: z.enum(['ok', 'down']),
})
export const FlagsResponse = FlagsSchema
export const AcceptedResponse = z.object({ queued: z.literal(true) })
